// ToastProvider – shows app-level toasts at the bottom of the window.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info, X, ExternalLink } from 'lucide-react';

type Toast = {
  message: string;
  type?: 'error' | 'warning' | 'info';
  requestId?: string;
};

const AUTO_DISMISS_MS = 8000;
const TOKEN_SUPPRESSION_WINDOW_MS = 2000;

const TOAST_STYLES: Record<
  NonNullable<Toast['type']>,
  { panelClass: string; icon: typeof AlertCircle; iconClass: string }
> = {
  error: {
    panelClass:
      'border-red-200 bg-red-50 dark:border-red-500/40 dark:bg-red-950/90',
    icon: AlertCircle,
    iconClass: 'text-red-600 dark:text-red-400',
  },
  warning: {
    panelClass:
      'border-amber-200 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-950/90',
    icon: AlertTriangle,
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  info: {
    panelClass:
      'border-blue-200 bg-blue-50 dark:border-blue-500/40 dark:bg-slate-900',
    icon: Info,
    iconClass: 'text-blue-600 dark:text-blue-400',
  },
};

export default function ToastProvider() {
  const [toast, setToast] = useState<Toast | null>(null);
  const tokenSuppressionRef = useRef(false);
  const dismissTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const clearToast = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setToast(null);
  }, []);

  const showToast = useCallback((msg: string, type: Toast['type'] = 'error', requestId?: string) => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setToast({ message: msg, type, requestId });
    dismissTimerRef.current = window.setTimeout(() => setToast(null), AUTO_DISMISS_MS);
  }, []);

  const handleError = useCallback(
    (msg: string) => {
      const lower = msg.toLowerCase();
      const isAuthError =
        lower.includes('token') ||
        lower.includes('openid') ||
        lower.includes('unauthorized') ||
        lower.includes('expired');

      if (isAuthError) {
        if (tokenSuppressionRef.current) return;
        tokenSuppressionRef.current = true;
        setTimeout(() => {
          tokenSuppressionRef.current = false;
        }, TOKEN_SUPPRESSION_WINDOW_MS);
        showToast('Invalid login token. Please sign in again.', 'error');
        return;
      }
      showToast(msg, 'error');
    },
    [showToast],
  );

  const handleInfo = useCallback(
    (payload: string | { message: string; requestId?: string }) => {
      if (typeof payload === 'string') {
        showToast(payload, 'info');
        return;
      }
      showToast(payload.message, 'info', payload.requestId);
    },
    [showToast],
  );

  const handleWarning = useCallback((msg: string) => showToast(msg, 'warning'), [showToast]);
  const handleDirectError = useCallback((msg: string) => showToast(msg, 'error'), [showToast]);

  useEffect(() => {
    window.errorAPI.onShowError(handleError);
    return () => window.errorAPI.removeShowErrorListener(handleError);
  }, [handleError]);

  useEffect(() => {
    const info = (e: Event) => {
      const ev = e as CustomEvent;
      if (ev.detail) handleInfo(ev.detail);
    };
    const warning = (e: Event) => {
      const ev = e as CustomEvent<string>;
      if (ev.detail) handleWarning(ev.detail);
    };
    const error = (e: Event) => {
      const ev = e as CustomEvent<string>;
      if (ev.detail) handleDirectError(ev.detail);
    };
    window.addEventListener('toast-info', info);
    window.addEventListener('toast-warning', warning);
    window.addEventListener('toast-error', error);
    return () => {
      window.removeEventListener('toast-info', info);
      window.removeEventListener('toast-warning', warning);
      window.removeEventListener('toast-error', error);
    };
  }, [handleInfo, handleWarning, handleDirectError]);

  if (!toast) return null;

  const variant = TOAST_STYLES[toast.type ?? 'error'];
  const Icon = variant.icon;

  const handleViewCommands = () => {
    if (!toast.requestId) return;
    navigate(`/commands/${toast.requestId}`);
    clearToast();
  };

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-[72px] z-[9999] flex justify-center px-4">
      <div
        className={`pointer-events-auto flex w-full max-w-sm animate-slide-up items-start gap-3 rounded-xl border px-4 py-3.5 shadow-lg backdrop-blur-md ${variant.panelClass}`}
      >
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${variant.iconClass}`} aria-hidden />
        <span className="flex-1 text-sm leading-snug text-gray-900 dark:text-slate-200">
          {toast.message}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {toast.requestId ? (
            <button
              type="button"
              onClick={handleViewCommands}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <ExternalLink className="h-3 w-3" />
              Commands
            </button>
          ) : null}
          <button
            type="button"
            onClick={clearToast}
            className="text-gray-500 transition-colors hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-100"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
